// The Billing tab's "Invoices" card: the customer's billing periods by month (GET
// /v1/orgs/{orgId}/billing-periods), with billed / AWS cost / profit from one revenue report
// (GET /v1/admin/revenue/by-month?orgId=); a month's row opens its summary with the invoice (or a
// live preview), its usage adjustments, and Close month (`billing:write`, the Invoices screen's
// close dialog).
import { useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { currentMonth } from "@nexus/ui";
import { Card, Chip } from "@nexus/ui";
import { ROW_OPEN_CLASS, rowClickHandler } from "@nexus/ui";
import { RowDetail } from "@nexus/ui";
import { Link } from "../../ui/shell";
import { ErrorBanner, Loading } from "@nexus/ui";
import { Money } from "@nexus/ui";
import { CloseDialog } from "../billing/close-dialog";
import {
  CLOSE_DIALOG_CLOSED,
  closeFailed,
  closeRequestBody,
  openCloseDialog,
  setForce,
  setReason,
  startClosing,
  type CloseDialogState,
} from "../billing/model";
import {
  adjustmentsPath,
  INVOICES_SHOWN,
  invoicePath,
  invoiceRows,
  revenueWindow,
  type BillingPeriod,
  type InvoiceRow,
  type RevenueBreakdown,
} from "./model";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

/** The month's billed, AWS cost and profit as the summary's facts. */
function figureFacts(row: InvoiceRow) {
  if (row.figures.kind === "billed") {
    return [
      { label: "Billed", value: <Money micro={row.figures.billedMicro} /> },
      { label: "AWS cost", value: <Money micro={row.figures.costMicro} /> },
      { label: "Profit", value: <Money micro={row.figures.profitMicro} /> },
    ];
  }
  return [{ label: "Billed", value: row.figures.kind === "not-billed" ? "Not billed" : "—" }];
}

function Figures({ row }: { row: InvoiceRow }) {
  if (row.figures.kind === "billed") {
    return (
      <>
        <td class="numeric">
          <Money micro={row.figures.billedMicro} />
        </td>
        <td class="numeric cell-cost">
          <Money micro={row.figures.costMicro} />
        </td>
        <td class="numeric cell-profit">
          <Money micro={row.figures.profitMicro} />
        </td>
      </>
    );
  }
  return (
    <>
      <td class="numeric muted">{row.figures.kind === "not-billed" ? "Not billed" : "—"}</td>
      <td class="numeric muted">—</td>
      <td class="numeric muted">—</td>
    </>
  );
}

export function InvoicesCard({ orgId, canWrite }: { orgId: string; canWrite: boolean }) {
  const client = useApi();
  const [periods, setPeriods] = useState<BillingPeriod[] | null>(null);
  const [revenue, setRevenue] = useState<RevenueBreakdown | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [dialog, setDialog] = useState<CloseDialogState>(CLOSE_DIALOG_CLOSED);
  const [selected, setSelected] = useState<InvoiceRow | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    client
      .get("/v1/orgs/{orgId}/billing-periods", { path: { orgId }, query: { limit: 500 } })
      .then((page) => {
        if (cancelled) return;
        setPeriods(page.items);
        const window = revenueWindow(page.items);
        if (!window) return;
        client
          .get("/v1/admin/revenue/by-month", { query: { ...window, orgId } })
          .then((report) => !cancelled && setRevenue(report))
          .catch(() => !cancelled && setRevenue(null));
      })
      .catch((err: unknown) => !cancelled && setError(unexpected(err)));
    return () => {
      cancelled = true;
    };
  }, [client, orgId, reloadToken]);

  const reload = () => setReloadToken((token) => token + 1);

  const confirmClose = async () => {
    setDialog(startClosing(dialog));
    try {
      await client.post("/v1/admin/orgs/{orgId}/billing-periods/{period}/close", {
        path: { orgId: dialog.orgId, period: dialog.period },
        body: closeRequestBody(dialog),
      });
      setDialog(CLOSE_DIALOG_CLOSED);
      reload();
    } catch (err) {
      setDialog((current) => closeFailed(current, unexpected(err)));
    }
  };

  const month = currentMonth(new Date());
  const rows = periods ? invoiceRows(periods, revenue, month, canWrite) : [];
  const shown = showAll ? rows : rows.slice(0, INVOICES_SHOWN);
  const openPeriod = rows.find((row) => row.open)?.period ?? month;

  return (
    <Card
      title="Invoices"
      help="terms.invoices"
      actions={
        canWrite && (
          <Link href={`${adjustmentsPath(orgId, openPeriod)}?new=1`} class="button button-sm">
            Add usage adjustment
          </Link>
        )
      }
    >
      {error ? (
        <ErrorBanner error={error} onRetry={reload} />
      ) : !periods ? (
        <Loading label="Loading invoices…" />
      ) : rows.length === 0 ? (
        <p class="card-note">No months yet. The first month opens when the customer's usage first arrives.</p>
      ) : (
        <>
          <div class="table-scroll">
            <table class="data-table">
              <caption class="visually-hidden">Invoices by month</caption>
              <thead>
                <tr>
                  <th scope="col">Month</th>
                  <th scope="col">Status</th>
                  <th scope="col" class="numeric">
                    Billed
                  </th>
                  <th scope="col" class="numeric">
                    AWS cost
                  </th>
                  <th scope="col" class="numeric">
                    Profit
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={row.period} class="row-clickable" onClick={rowClickHandler(() => setSelected(row))}>
                    <td class="invoice-month">
                      <button type="button" class={ROW_OPEN_CLASS} aria-label={`${row.monthLabel}, ${row.status.label}`} />
                      {row.monthLabel}
                    </td>
                    <td>
                      <Chip tone={row.status.tone}>{row.status.label}</Chip>
                      {row.forced && <span class="cell-sub">Forced close</span>}
                    </td>
                    <Figures row={row} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > INVOICES_SHOWN && (
            <div class="row">
              <button type="button" class="button-link" aria-expanded={showAll} onClick={() => setShowAll((all) => !all)}>
                {showAll ? `Show the last ${INVOICES_SHOWN} months` : `Show all ${rows.length} months`}
              </button>
            </div>
          )}
          <p class="card-note">Billed, AWS cost (at AWS list price) and profit come from the revenue report; an open month is a preview to date.</p>
        </>
      )}

      {selected && (
        <RowDetail
          title={selected.monthLabel}
          open
          onClose={() => setSelected(null)}
          facts={[
            {
              label: "Status",
              value: (
                <>
                  <Chip tone={selected.status.tone}>{selected.status.label}</Chip>
                  {selected.forced && <span class="cell-sub">Forced close</span>}
                </>
              ),
            },
            ...figureFacts(selected),
          ]}
          actions={
            <>
              <Link href={adjustmentsPath(orgId, selected.period)} class="button button-secondary">
                Adjustments
              </Link>
              <Link href={invoicePath(orgId, selected.period)} class="button button-secondary">
                {selected.open ? "Preview" : "View invoice"}
              </Link>
              {selected.canClose && (
                <button
                  type="button"
                  class="button button-primary"
                  onClick={() => {
                    setSelected(null);
                    setDialog(openCloseDialog(orgId, selected.period));
                  }}
                >
                  Close month
                </button>
              )}
            </>
          }
        />
      )}

      <CloseDialog
        state={dialog}
        period={periods?.find((p) => p.period === dialog.period) ?? null}
        onForce={(force) => setDialog((current) => setForce(current, force))}
        onReason={(reason) => setDialog((current) => setReason(current, reason))}
        onConfirm={() => void confirmClose()}
        onClose={() => setDialog(CLOSE_DIALOG_CLOSED)}
      />
    </Card>
  );
}
